import { createContext, useEffect, useRef, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";

import { supabaseClient } from "@config/supabaseClient";
import type { AuthError } from "../schemas";
import {
	signInWithEmail,
	signUpWithEmail,
	signOut as apiSignOut,
} from "../api";
import { clearSubscriptionApiCache } from "@/features/subscriptions";

interface AuthContextType {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signIn: (
		email: string,
		password: string,
	) => Promise<{ error: AuthError | null }>;
	signUp: (
		email: string,
		password: string,
	) => Promise<{ error: AuthError | null }>;
	signOut: () => Promise<void>;
}

// Context is internal only - not exported
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export for use in hook file only
export { AuthContext };

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);
	const previousUserIdRef = useRef<string | null>(null);

	useEffect(() => {
		const syncSubscriptionCache = (nextUserId: string | null) => {
			if (previousUserIdRef.current !== nextUserId) {
				clearSubscriptionApiCache();
				queryClient.removeQueries({ queryKey: ["subscription"] });
			}
			previousUserIdRef.current = nextUserId;
		};

		// Get initial session
		supabaseClient.auth.getSession().then(({ data: { session } }) => {
			syncSubscriptionCache(session?.user?.id ?? null);
			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		});

		// Listen for auth changes
			const {
				data: { subscription },
			} = supabaseClient.auth.onAuthStateChange((_event, session) => {
				syncSubscriptionCache(session?.user?.id ?? null);
				setSession(session);
				setUser(session?.user ?? null);
				setLoading(false);
			});

			return () => subscription.unsubscribe();
		}, [queryClient]);

	const signIn = async (email: string, password: string) => {
		const { error } = await signInWithEmail({
			email,
			password,
			rememberMe: false,
		});
		return { error };
	};

	const signUp = async (email: string, password: string) => {
		// For OAuth, try to extract names from user metadata if available
		const firstName =
			user?.user_metadata?.firstName || user?.user_metadata?.given_name || "";
		const lastName =
			user?.user_metadata?.lastName || user?.user_metadata?.family_name || "";

		const { error } = await signUpWithEmail({
			email,
			password,
			firstName,
			lastName,
			confirmPassword: password,
			acceptTerms: true,
		});
		return { error };
	};

		const signOut = async () => {
			clearSubscriptionApiCache();
			queryClient.removeQueries({ queryKey: ["subscription"] });
			await apiSignOut();
		};

	return (
		<AuthContext.Provider
			value={{ user, session, loading, signIn, signUp, signOut }}
		>
			{children}
		</AuthContext.Provider>
	);
}
